import {
  Apple,
  Auth,
  Entry,
  Google,
  Id,
  MailGrey,
  X,
} from "@/components/bnp-icons";
import { IconEye, IconEyeOff } from "@tabler/icons-react";
import { Button } from "@/components/bnp-ui";
import AuthWrapper from "@/components/layouts/AuthWrapper";
import * as yup from "yup";
import { AuthService } from "@/services";
import { useMutation } from "@tanstack/react-query";

import { useCompatRouter as useRouter } from "@/lib/compat-router/context";
import { toast } from "react-toastify";
import { useCallback, useRef, useState } from "react";
import parseHttpError from "@/utils/parseHttpError";
import { PhoneInput } from "@/components/bnp-ui/page_utils/PhoneNumberInput";
import { usePostHogAnalytics } from "@/contexts/PostHogAnalyticsContext";
import { trackRoleFunnelEvent } from "@/utils/roleAnalytics";

// Common Reusable Input Styles with #2D96A7 Active Brand Color Focus
const inputClass =
  "w-full h-[44px] px-3.5 py-2 bg-white border border-gray-300 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-[#2D96A7] focus:ring-2 focus:ring-[#2D96A7]/20 transition-all";

const validationSchema = yup.object().shape({
  email: yup.string().email("Enter a valid email").required("Enter your email"),
  first_name: yup.string().required("Enter your first name"),
  last_name: yup.string().required("Enter your last name"),
  phone_number: yup.string().required("Enter your phone number"),
  password: yup
    .string()
    .min(8, "Password must be at least 8 characters")
    .required("Enter a password"),
  confirmPassword: yup
    .string()
    .required("Please confirm your password")
    .oneOf([yup.ref("password")], "Passwords must match"),
  role: yup.string().required("Select your role"),
  termsAccepted: yup
    .boolean()
    .oneOf([true], "You must accept the Terms & Conditions")
    .required("You must accept the Terms & Conditions"),
  emailMarketingConsent: yup.boolean(),
});

const SignUp = () => {
  const router = useRouter();
  const ph = usePostHogAnalytics();

  const [formData, setFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
    phone_number: "",
    password: "",
    confirmPassword: "",
    role: "",
    termsAccepted: false,
    emailMarketingConsent: true,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneCheckMsg, setPhoneCheckMsg] = useState("");
  const phoneCheckTimer = useRef<NodeJS.Timeout | null>(null);
  const signupStartedRef = useRef(false);

  const [passwordStrength, setPasswordStrength] = useState({
    hasUpperCase: false,
    hasNumber: false,
    hasMinLength: false,
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
    // Funnel: signup started — once per mount, on first interaction.
    if (!signupStartedRef.current) {
      signupStartedRef.current = true;
      ph.trackAnalyticsEvent("signup_started", {});
    }
  };

  const checkPasswordStrength = (password: string) => {
    setPasswordStrength({
      hasUpperCase: /[A-Z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasMinLength: password.length >= 8,
    });
  };

  const handlePhoneChange = useCallback((fullNumber: string, isValid: boolean) => {
    handleInputChange("phone_number", fullNumber);
    setPhoneCheckMsg("");

    if (!isValid && fullNumber.length > 5) {
      setErrors((prev) => ({ ...prev, phone_number: "Invalid phone number for selected country" }));
      return;
    } else {
      setErrors((prev) => ({ ...prev, phone_number: "" }));
    }

    if (phoneCheckTimer.current) clearTimeout(phoneCheckTimer.current);
    if (isValid) {
      phoneCheckTimer.current = setTimeout(async () => {
        try {
          const res = await AuthService.checkPhoneAvailability(fullNumber);
          if (!res.data?.data?.available) {
            setPhoneCheckMsg("This phone number is already registered");
          }
        } catch {
          // ignore check errors
        }
      }, 600);
    }
  }, []);

  const signupRequest = async () => {
    const { termsAccepted, confirmPassword, ...payload } = formData;
    const trimmedPayload = {
      ...payload,
      email: payload.email.trim(),
      first_name: payload.first_name.trim(),
      last_name: payload.last_name.trim(),
      phone_number: payload.phone_number.trim(),
      password: payload.password.trim(),
    };
    const response = await AuthService.register(trimmedPayload);
    return response.data;
  };

  const mutation = useMutation({
    mutationFn: signupRequest,
    onSuccess: () => {
      // Fires generic `user_signed_up` + per-role
      // `regular_user_signed_up` / `realtor_signed_up` / `developer_signed_up`.
      trackRoleFunnelEvent(
        ph.trackEvent,
        "user_signed_up",
        "signed_up",
        formData.role,
        {
          method: "password",
        },
      );
      const encodedEmail = btoa(formData.email);
      router.push(`/auth/verify?token=${encodedEmail}`);
    },
    onError: (error: any) => {
      toast.error(error.message || "Sign up failed");
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});
      if (!phoneCheckMsg) {
        mutation.mutate();
      }
    } catch (err: any) {
      if (err instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
        setErrors(validationErrors);
      }
    }
  };

  return (
    <AuthWrapper
      footerText={"Already have an account?"}
      footerBtnText={"Sign in"}
      footerLink={"/auth/sign-in"}
      footerIcon={<Entry />}
    >
      <div className="flex flex-col w-full">
        <div className="flex flex-col items-center gap-4 w-full">
          <Auth />
          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-main-900 font-semibold text-xl">
              Create a new account
            </h2>
            <p className="text-grey-7 text-sm font-normal">
              Enter your details to register.
            </p>
          </div>
        </div>

        <div className="lg:w-full mt-4">
          <form
            autoComplete="off"
            className="flex flex-col gap-4"
            onSubmit={handleSubmit}
          >
            {/* Email Field */}
            <div className="flex flex-col gap-1.5 w-full">
              <label className="text-sm font-medium text-gray-700">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <span className="absolute left-3 text-gray-400">
                  <MailGrey />
                </span>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  placeholder="Email"
                  className={`${inputClass} pl-10 ${errors.email ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                />
              </div>
              {errors.email && (
                <span className="text-red-500 text-xs mt-1">{errors.email}</span>
              )}
            </div>

            {/* First & Last Name */}
            <div className="flex gap-4">
              <div className="flex flex-col gap-1.5 w-1/2">
                <label className="text-sm font-medium text-gray-700">
                  First Name <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-400">
                    <Id />
                  </span>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    placeholder="First Name"
                    className={`${inputClass} pl-10 ${errors.first_name ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                  />
                </div>
                {errors.first_name && (
                  <span className="text-red-500 text-xs mt-1">{errors.first_name}</span>
                )}
              </div>

              <div className="flex flex-col gap-1.5 w-1/2">
                <label className="text-sm font-medium text-gray-700">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <div className="relative flex items-center">
                  <span className="absolute left-3 text-gray-400">
                    <Id />
                  </span>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    placeholder="Last Name"
                    className={`${inputClass} pl-10 ${errors.last_name ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                  />
                </div>
                {errors.last_name && (
                  <span className="text-red-500 text-xs mt-1">{errors.last_name}</span>
                )}
              </div>
            </div>

            {/* Phone Number & Role */}
            <div className="">
              <div className="">
                <PhoneInput
                  value={formData.phone_number}
                  onChange={handlePhoneChange}
                  error={errors.phone_number || phoneCheckMsg}
                  inputClass={inputClass}
                  required
                />
              </div>
            </div>
            {/* Phone Number & Role */}
            <div className="">
              <div className="">
                <label className="text-sm font-medium text-gray-700">
                  Role <span className="text-red-500">*</span>
                </label>
                <select
                  name="role"
                  value={formData.role}
                  className={`${inputClass} ${errors.role ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                  onChange={(e) => handleInputChange("role", e.target.value)}
                >
                  <option value="">Select your role</option>
                  <option value="regular-user">
                    Others (Landlord/Buyer/Tenant, etc)
                  </option>
                  <option value="realtor">Realtor</option>
                  <option value="developer">Developer</option>
                </select>
                {errors.role && (
                  <span className="text-red-500 text-xs mt-1">{errors.role}</span>
                )}
              </div>
            </div>

            {/* Password Field */}
            <div className="flex flex-col gap-1.5 w-full">
              <label className="text-sm font-medium text-gray-700">
                Password <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  autoComplete="new-password"
                  value={formData.password}
                  placeholder="Create a password"
                  className={`${inputClass} pr-10 ${errors.password ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                  onChange={(e) => {
                    handleInputChange("password", e.target.value);
                    checkPasswordStrength(e.target.value);
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
              {errors.password && (
                <span className="text-red-500 text-xs mt-1">{errors.password}</span>
              )}
            </div>

            {/* Password Indicator */}
            {formData.password && (
              <div className="-mt-2">
                <div className="flex gap-3 text-xs">
                  <span className={passwordStrength.hasUpperCase ? "text-green-600" : "text-gray-400"}>
                    ✓ Uppercase
                  </span>
                  <span className={passwordStrength.hasNumber ? "text-green-600" : "text-gray-400"}>
                    ✓ Number
                  </span>
                  <span className={passwordStrength.hasMinLength ? "text-green-600" : "text-gray-400"}>
                    ✓ 8+ chars
                  </span>
                </div>
              </div>
            )}

            {/* Confirm Password Field */}
            <div className="flex flex-col gap-1.5 w-full">
              <label className="text-sm font-medium text-gray-700">
                Confirm Password <span className="text-red-500">*</span>
              </label>
              <div className="relative flex items-center">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  autoComplete="new-password"
                  value={formData.confirmPassword}
                  placeholder="Confirm your password"
                  className={`${inputClass} pr-10 ${errors.confirmPassword ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : ""}`}
                  onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <IconEyeOff size={18} /> : <IconEye size={18} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <span className="text-red-500 text-xs mt-1">{errors.confirmPassword}</span>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="flex flex-col gap-1 w-full">
              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="termsAccepted"
                  checked={formData.termsAccepted}
                  onChange={(e) => handleInputChange("termsAccepted", e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-[#2D96A7] focus:ring-[#2D96A7]"
                />
                <label htmlFor="termsAccepted" className="text-sm text-main-900 leading-tight">
                  I agree to the&nbsp;
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline text-bnp-cyan"
                  >
                    Terms & Conditions
                  </a>
                  &nbsp; and &nbsp;
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium underline text-bnp-cyan"
                  >
                    Privacy Policy.
                  </a>
                </label>
              </div>
              {errors.termsAccepted && (
                <span className="text-red-500 text-xs mt-1">{errors.termsAccepted}</span>
              )}
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="bg-bnp-dark text-white text-sm font-extrabold w-full h-[44px] rounded-lg mt-2"
              isLoading={mutation.isPending}
            >
              Create my account
            </Button>
          </form>
        </div>
      </div>
    </AuthWrapper>
  );
};

export default SignUp;